import { useEffect, useState } from "react";
import { MdTranslate } from "react-icons/md";
import { useSelector } from "react-redux";
import axios from "axios";
import toast from "react-hot-toast";

import ErrorMessage from "../../components/ErrorMessage";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "react-i18next";

const LanguageSettings: React.FC = () => {
  const [selectedLanguage, setSelectedLanguage] = useState<"en" | "ar">("ar");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [needRefetch, setNeedRefetch] = useState(false);
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );
  useEffect(() => {
    if (!token) return

    const fetchDefaultLang = async () => {
      const BASE_URL = import.meta.env.VITE_BASE_URL;
      const url = `${BASE_URL}/api/dashboard/settings/default-language?locale=${lang}`;

      setIsLoading(true);
      setError(null);

      setNeedRefetch(false);

      try {
        const response = await axios.get(url, {
          headers: {
            Authorization: `${token}`,
          },
        });
        setSelectedLanguage(response.data?.defaultLanguage || "ar");
      } catch (error: unknown) {
        if (axios.isAxiosError(error)) {
          const message =
            error.response?.data?.error ||
            (t("languageSettings.fetchFailed") as string )
          setError(message);
          toast.error(message);
        } else {
          setError(
            t("languageSettings.unexpectedError") as string,
          );
          toast.error(
            t("languageSettings.unexpectedError") as string,
          );
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDefaultLang();
  }, [lang, token, needRefetch, t]);

  const handleChangeDefaultLanguage = async () => {
    const BASE_URL = import.meta.env.VITE_BASE_URL;
    const url = `${BASE_URL}/api/dashboard/settings/default-language?locale=${lang}`;

    const LoadingToastId = toast.loading(
      t("languageSettings.updating") as string,
    );

    try {
      const response = await axios.put(
        url,
        { defaultLanguage: selectedLanguage },
        {
          headers: {
            Authorization: `${token}`,
          },
        },
      );


      if (!response.data.success) {
        toast.error(
          t("languageSettings.updateFailed") as string,
        );
        return;
      }

      toast.success(
        t("languageSettings.updateSuccess") as string,
      );
      setSelectedLanguage(selectedLanguage);
      setNeedRefetch(true);
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        const message =
          error.response?.data?.error ||
          (t("languageSettings.updateFailed") as string);
        toast.error(message);
      } else {
        console.error(error);
      }
    } finally {
      toast.dismiss(LoadingToastId);
    }
  };

  if (isLoading) return <>
    <div className="flex flex-wrap mt-10 items-center justify-between gap-2">
      <Skeleton className="w-[300px] dark:bg-slate-600 dark:bg-opacity-35  h-[200px]" />
      <Skeleton className="w-[600px] dark:bg-slate-600 dark:bg-opacity-35  h-[30px]" />
    </div>
  </>;
  if (error) return <ErrorMessage msg={error} />;

  return (
    <div className="flex flex-col gap-4">
      <h1 className="self-start text-2xl font-semibold">
        {t("languageSettings.title")}
      </h1>

      <div className="flex flex-col gap-5 rounded-md border border-gray-300 px-4 py-6">
        <div className="flex items-center gap-3">
          <MdTranslate className="text-xl text-gray-700" />
          <span className="text-xl">
            {t("languageSettings.storeLanguages")}
          </span>
        </div>
        {/* Default store language */}
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-lg font-semibold capitalize text-gray-700">
            {t("languageSettings.defaultStoreLanguage")}
          </h1>
          <select
            className="h-10 w-full min-w-[50%] rounded-sm border border-gray-300 bg-white px-4 py-2 text-sm shadow-sm hover:border-gray-400 sm:w-auto"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value as "en" | "ar")}
          >
            <option value="ar">العربية</option>
            <option value="en">English</option>
          </select>
        </div>

        {/* save */}
        <button
          onClick={handleChangeDefaultLanguage}
          className="self-start rounded-md bg-sidebar px-5 py-2 text-base font-medium text-skyblue"
        >
          {t("save")}
        </button>
      </div>
    </div>
  );
};

export default LanguageSettings;
